import { formatNumber } from '../../helpers';
import { StatsTxtProps } from '../../types';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { formatVersion } from '../../helpers';

const BulletItem: React.FC<{ children: React.ReactNode; isSubitem?: boolean }> = ({
  children,
  isSubitem = false,
}) => (
  <>
    {isSubitem ? (
      <div className="row">
        <ChevronRightIcon style={{ width: '1rem ', color: 'var(--pink-color)' }}></ChevronRightIcon>
        <li>{children}</li>
      </div>
    ) : (
      <li className="bullet-item">{children}</li>
    )}
  </>
);

export const StatsTxt = ({ statistics, version }: StatsTxtProps) => {
  if (!statistics) {
    return <p>Statistics not available</p>;
  }

  return (
    <div>
      <p>The current version of DISGENET ({formatVersion(version as string)}) contains:</p>
      <ul>
        <BulletItem>
          <b>{formatNumber(statistics.gda_summary)}</b> gene-disease associations (GDAs), between{' '}
          <b>{formatNumber(statistics.gene)}</b> genes and{' '}
          <b>{formatNumber(statistics.gda_disease ? statistics.gda_disease : statistics.disease)}</b> diseases
          and phenotypes
          <ul>
            <BulletItem isSubitem>
              <b>{formatNumber(statistics.gda_evidence)}</b> evidences for GDAs, supported by{' '}
              <b>{formatNumber(statistics.gda_publication)}</b> publications
            </BulletItem>
            <BulletItem isSubitem>
              <b>{formatNumber(statistics.gda_animal_model)}</b> associations involving{' '}
              <b>{formatNumber(statistics.gda_animal_model_disease)}</b> diseases that have been detected in
              animal models
            </BulletItem>
            <BulletItem isSubitem>
              <b>{formatNumber(statistics.gda_chemical)}</b> GDAs are annotated with{' '}
              <b>{formatNumber(statistics.chemical_gda)}</b> chemicals (relating{' '}
              <b>{formatNumber(statistics.gda_chemical_gene)}</b> genes and{' '}
              <b>{formatNumber(statistics.gda_chemical_disease)}</b> diseases)
            </BulletItem>
          </ul>
        </BulletItem>
        <BulletItem>
          <b>{formatNumber(statistics.vda_summary)}</b> variant-disease associations (VDAs), between{' '}
          <b>{formatNumber(statistics.vda_variant)}</b> variants and{' '}
          <b>{formatNumber(statistics.vda_disease)}</b> diseases and phenotypes.
          <ul>
            <BulletItem isSubitem>
              <b>{formatNumber(statistics.vda_evidence)}</b> evidences for VDAs, supported by{' '}
              <b>
                {formatNumber(
                  statistics.vda_publications ? statistics.vda_publications : statistics.vda_publication,
                )}
              </b>{' '}
              publications
            </BulletItem>
            <BulletItem isSubitem>
              <b>{formatNumber(statistics.vda_chemical)}</b> VDAs are annotated with{' '}
              <b>{formatNumber(statistics.chemical_vda)}</b> chemicals (relating{' '}
              <b>{formatNumber(statistics.vda_chemical_variant)}</b> variants and{' '}
              <b>{formatNumber(statistics.vda_chemical_disease)}</b> diseases)
            </BulletItem>
          </ul>
        </BulletItem>
        <BulletItem>
          There are <b>{formatNumber(statistics.dda_summary ? statistics.dda_summary : statistics.dda)}</b>{' '}
          disease-disease associations.
        </BulletItem>
        <BulletItem>
          This release provides annotations for{' '}
          <b>{formatNumber(statistics.chemical ? statistics.chemical : statistics.chemicals_gda_or_vda)}</b>{' '}
          chemicals and drugs associated with GDAs and VDAs.
        </BulletItem>
        <BulletItem>
          In total, DISGENET contains a corpus of <b>{formatNumber(statistics.publication)}</b> publications
          that support the genotype-phenotype associations.
        </BulletItem>
        <BulletItem>
          There are <b>{formatNumber(statistics.evidence)}</b> individual pieces of evidence in the database
          supporting <b>{formatNumber(statistics.gda_or_vda)}</b> genotype-phenotype associations (GDAs &
          VDAs)
        </BulletItem>
        {/* Appears in version info only (25.3) */}
        {version === 'v253' && (
          <BulletItem>
            There are <b>{formatNumber(statistics.genes_in_gda_vda)}</b> genes if we take into account the
            genes related to the variants
          </BulletItem>
        )}
      </ul>
    </div>
  );
};
